import { pick, shake } from "radash"
import { array, boolean, mixed, number, object, string } from "yup"

import type { FormFieldsFragment } from "~graphql/common.generated"
import { FormInputType, type CreateFormInput } from "~graphql/generated/types"

import { getTempId } from "./create-form-helpers"
import type {
    OutfitterFormBuilderDraft,
    OutfitterFormBuilderDraftInput,
} from "./outfitter-form-builder-draft-types"
import {
    CREATE_FORM_BASICS_FORM_KEYS,
    CREATE_FORM_DETAILS_FORM_KEYS,
    getFormEditorDefaultValues,
    type FormEditorFormData,
} from "./form-form-helpers"

const NON_BLANK_REGEX = /\S/

const builderDraftFormInputSchema = object({
    id: string(),
    input_type: mixed<FormInputType>()
        .oneOf(Object.values(FormInputType))
        .required(),
    title: string().required().matches(NON_BLANK_REGEX),
    options: array().of(string().required()),
    placeholder: string(),
    is_required: boolean().required(),
    additional_notes: string(),
    should_allow_pricing_package_selection: boolean(),
    order: number(),
})

const builderDraftLinkToBookingConfigsSchema = object({
    listings: array().of(string().required()),
    pricing_packages: array().of(string().required()),
    has_to_be_filled_per_member: boolean(),
}).default(undefined)

const builderDraftSchema = object({
    name: string(),
    description: string(),
    form_inputs: array().of(builderDraftFormInputSchema),
    is_required: boolean(),
    should_create_lead_from_submission: boolean(),
    success_redirect_url: string().nullable(),
    link_to_booking_configs: builderDraftLinkToBookingConfigsSchema,
})

// `satisfies keyof CreateFormInput` keeps every picked key a real GraphQL form field,
// so the parsed draft can't drift from the server tool or the create/update mutation.
const BUILDER_DRAFT_KEYS = [
    "name",
    "description",
    "form_inputs",
    "is_required",
    "should_create_lead_from_submission",
    "success_redirect_url",
    "link_to_booking_configs",
] as const satisfies readonly (keyof CreateFormInput)[]

export function mergeOutfitterFormBuilderDraftIntoValues({
    form,
    draft,
}: {
    form: FormFieldsFragment | undefined
    draft: OutfitterFormBuilderDraft
}): FormEditorFormData {
    const baseValues = getFormEditorDefaultValues({ form })

    if (draft.name !== undefined) {
        baseValues[CREATE_FORM_BASICS_FORM_KEYS.NAME] = draft.name
    }

    if (draft.description !== undefined) {
        baseValues[CREATE_FORM_BASICS_FORM_KEYS.DESCRIPTION] = draft.description
    }

    if (draft.is_required !== undefined) {
        baseValues[CREATE_FORM_DETAILS_FORM_KEYS.IS_REQUIRED] =
            draft.is_required
    }

    if (draft.should_create_lead_from_submission !== undefined) {
        baseValues[
            CREATE_FORM_DETAILS_FORM_KEYS.SHOULD_CREATE_LEAD_FROM_SUBMISSION
        ] = draft.should_create_lead_from_submission
    }

    if (draft.success_redirect_url !== undefined) {
        baseValues[CREATE_FORM_DETAILS_FORM_KEYS.SUCCESS_REDIRECT_URL] =
            draft.success_redirect_url ?? ""
    }

    if (draft.form_inputs !== undefined) {
        baseValues[CREATE_FORM_DETAILS_FORM_KEYS.FORM_INPUTS] =
            draft.form_inputs.map(formatBuilderDraftFormInput)
    }

    if (draft.link_to_booking_configs !== undefined) {
        applyLinkToBookingBuilderDraft({
            baseValues,
            linkToBookingConfigs: draft.link_to_booking_configs,
        })
    }

    return baseValues
}

function formatBuilderDraftFormInput(input: OutfitterFormBuilderDraftInput) {
    return {
        id: input.id?.trim() ? input.id : getTempId(),
        input_type: input.input_type,
        title: input.title,
        is_required: input.is_required,
        options: input.options ? [...input.options] : undefined,
        placeholder: input.placeholder,
        additional_notes: input.additional_notes,
        order: input.order,
        should_allow_pricing_package_selection:
            input.should_allow_pricing_package_selection,
    }
}

function applyLinkToBookingBuilderDraft({
    baseValues,
    linkToBookingConfigs,
}: {
    baseValues: FormEditorFormData
    linkToBookingConfigs: NonNullable<
        OutfitterFormBuilderDraft["link_to_booking_configs"]
    >
}) {
    const listings = linkToBookingConfigs.listings ?? []
    const pricingPackages = linkToBookingConfigs.pricing_packages ?? []
    const shouldAddFormToBooking =
        listings.length > 0 || pricingPackages.length > 0

    baseValues[CREATE_FORM_DETAILS_FORM_KEYS.SHOULD_ADD_FORM_TO_BOOKING] =
        shouldAddFormToBooking
    baseValues[CREATE_FORM_DETAILS_FORM_KEYS.LISTING_IDS] = [...listings]
    baseValues[CREATE_FORM_DETAILS_FORM_KEYS.PRICING_PACKAGE_IDS] = [
        ...pricingPackages,
    ]

    if (linkToBookingConfigs.has_to_be_filled_per_member !== undefined) {
        baseValues[CREATE_FORM_DETAILS_FORM_KEYS.HAS_TO_BE_FILLED_PER_MEMBER] =
            linkToBookingConfigs.has_to_be_filled_per_member
    }
}

export function parseOutfitterFormBuilderDraft(
    value: unknown
): OutfitterFormBuilderDraft | null {
    if (typeof value !== "object" || value === null) return null

    try {
        // strict: validate without coercion so mistyped fields reject the
        // whole draft instead of being silently cast
        const validated = builderDraftSchema.validateSync(value, {
            strict: true,
        })
        const draft: OutfitterFormBuilderDraft = shake(
            pick(validated, [...BUILDER_DRAFT_KEYS])
        )

        return Object.keys(draft).length ? draft : null
    } catch {
        return null
    }
}
